import { TypedDocumentNode, gql } from '@apollo/client';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Position } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelXS } from '@sorare/core/src/atoms/typography';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import { MultipleMatchupItem } from '@sorare/marketplace/src/components/benchObject/MultipleMatchupItem';
import { SingleMatchupItem } from '@sorare/marketplace/src/components/benchObject/SingleMatchupItem';

import { Matchups_playerGameScoreInterface } from './__generated__/index.graphql';

const Item = styled(Horizontal).attrs({ gap: 0.5, center: true })`
  width: 100%;
  height: 100%;
  color: var(--c-nd-600);

  &.highlighted {
    background-color: var(--c-nd-50);
    color: var(--c-white);
  }

  &:not(:last-child) {
    border-right: 1px solid var(--c-black);
  }
`;

type Props = {
  position: Position;
  playerGameScores: Matchups_playerGameScoreInterface[];
};

export const Matchups = ({
  position,
  playerGameScores: playerGameScoresProp,
}: Props) => {
  const playerGameScores = playerGameScoresProp.filter(
    pgs => pgs.position === position
  );

  if (playerGameScores.length === 0) {
    return [
      <Item key={1}>
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          color="var(--c-red-600"
          width={10}
        />
        <LabelXS>
          <FormattedMessage {...glossary.noGame} />
        </LabelXS>
      </Item>,
    ];
  }

  if (
    playerGameScores.length === 1 &&
    isType(playerGameScores[0].anyGame, 'Game')
  ) {
    const pgs = playerGameScores[0];
    const playerTeam = pgs.anyPlayerGameStats.anyTeam;

    return [
      <SingleMatchupItem
        key={pgs.id}
        anyGame={pgs.anyGame}
        playerTeamSlug={playerTeam?.slug}
      />,
    ];
  }

  return playerGameScores.map(pgs => {
    const playerTeamSlug = pgs.anyPlayerGameStats.anyTeam.slug;
    return (
      <MultipleMatchupItem
        key={pgs.id}
        anyGame={pgs.anyGame}
        playerSlug={pgs.anyPlayer.slug}
        playerTeamSlug={playerTeamSlug}
      />
    );
  });
};

Matchups.fragments = {
  playerGameScoreInterface: gql`
    fragment Matchups_playerGameScoreInterface on PlayerGameScoreInterface {
      id
      position: positionTyped
      anyPlayer {
        slug
      }
      anyPlayerGameStats {
        id
        anyTeam {
          slug
        }
      }
      anyGame {
        id
        ...SingleMatchupItem_anyGame
        ...MultipleMatchupItem_anyGame
      }
    }
    ${SingleMatchupItem.fragments.anyGame}
    ${MultipleMatchupItem.fragments.anyGame}
  ` as TypedDocumentNode<Matchups_playerGameScoreInterface>,
};
